import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactionTest from '../tests/ReactionTest';
import AimTest from '../tests/AimTest';
import TrackingTest from '../tests/TrackingTest';
import MemoryTest from '../tests/MemoryTest';
import FlickerTest from '../tests/FlickerTest';
import SequencingTest from '../tests/SequencingTest';
import ComposureTest from '../tests/ComposureTest';
import TiltTest from '../tests/TiltTest';
import { getTrialSequence, submitModule, completeSession, adminAutoComplete } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GENRE_MODULES = {
  general: ['reaction', 'aim', 'tracking', 'memory', 'composure', 'tilt'],
  fps:     ['reaction', 'aim', 'tracking', 'flicker', 'composure', 'tilt'],
  moba:    ['reaction', 'sequencing', 'tracking', 'memory', 'composure', 'tilt'],
};

const MODULE_ICONS = {
  reaction: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>,
  aim:      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /></svg>,
  tracking: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-6.22-8.56" strokeDasharray="3 2.2" /><circle cx="18" cy="6" r="2.2" fill="currentColor" fillOpacity="0.2" /><path d="M12 12 L18 6" strokeWidth="1.2" opacity="0.5" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /></svg>,
  memory:   <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" opacity="0.45" /><rect x="3" y="14" width="7" height="7" rx="1.5" opacity="0.45" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><path d="M10 6.5h4M17.5 10v4M14 17.5h-4M6.5 14v-4" strokeWidth="1.1" opacity="0.5" strokeDasharray="1.5 1" /></svg>,
  flicker:  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="2.5" fill="currentColor" fillOpacity="0.2" /><path d="M12 3v3.5M12 17.5v3.5M3 12h3.5M17.5 12H21" /><path d="M6.4 6.4l2.3 2.3M15.3 15.3l2.3 2.3M6.4 17.6l2.3-2.3M15.3 8.7l2.3-2.3" strokeWidth="1.4" /></svg>,
  sequencing: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="5" r="2.5" /><circle cx="19" cy="5" r="2.5" /><circle cx="19" cy="19" r="2.5" /><circle cx="5" cy="19" r="2.5" /><path d="M7.5 5h9M19 7.5v9M16.5 19h-9M5 16.5v-9" strokeWidth="1.2" strokeDasharray="2 1.5" opacity="0.5" /></svg>,
  composure: <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h4M10 12h4M20 12h-2" /><path d="M14 9l3 3-3 3" /><circle cx="12" cy="12" r="9" strokeOpacity="0.3" /><path d="M7 9l-3 3 3 3" strokeOpacity="0.4" /><path d="M17 9l3 3-3 3" strokeOpacity="0.4" /></svg>,
  tilt:      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l4-8 4 5 3-3 4 6" /><circle cx="19" cy="6" r="2" fill="currentColor" fillOpacity="0.2" /><path d="M19 4v4M17 6h4" strokeWidth="1.2" /></svg>,
};

const MODULE_LABELS = {
  reaction:   'Reaction & Precision',
  aim:        'Aim Precision',
  tracking:   'Target Tracking',
  memory:     'Working Memory',
  flicker:    'Flick Speed',
  sequencing: 'Target Sequencing',
  composure:  'Composure Under Fire',
  tilt:       'Tilt Recovery',
};

const MODULE_COLORS = {
  reaction:   '#00d4ff',
  aim:        '#ef4444',
  tracking:   '#10b981',
  memory:     '#a855f7',
  flicker:    '#ff6b35',
  sequencing: '#f59e0b',
  composure:  '#00d4ff',
  tilt:       '#ef4444',
};

const MODULE_DESCS = {
  reaction:   'Measures raw neural processing speed and impulse control.',
  aim:        'Hit rate and center accuracy on randomised mouse targets.',
  tracking:   'Sustained cursor control on a Lissajous-path moving target.',
  memory:     'Spatial grid recall — reproduce a flashed cell sequence in the correct order.',
  flicker:    'Hit rate and reaction time on 200ms disappearing targets.',
  sequencing: 'Multi-target completion speed under time pressure.',
  composure:  'Decision accuracy while ignoring flanker distractors.',
  tilt:       'Bet calibration and accuracy recovery after a simulated losing streak.',
};

function computeBreakStat(moduleName, results) {
  if (!results || results.length === 0) return null;
  if (moduleName === 'reaction') {
    const valid = results.filter((r) => r.type === 'simple' && r.correct && r.rt);
    if (!valid.length) return null;
    const avg = Math.round(valid.reduce((s, r) => s + r.rt, 0) / valid.length);
    return `Avg reaction time: ${avg}ms`;
  }
  if (moduleName === 'aim') {
    const hits = results.filter((r) => r.correct).length;
    return `${hits} / ${results.length} targets hit`;
  }
  if (moduleName === 'tracking') {
    const pct = Math.round((results[0]?.timeOnTarget || 0) * 100);
    return `${pct}% time on target`;
  }
  if (moduleName === 'memory') {
    const totalCells = results.reduce((s, r) => s + (r.sequenceLength || 0), 0);
    const correctCells = results.reduce((s, r) => s + (r.correctCount || 0), 0);
    if (!totalCells) return null;
    return `${correctCells} / ${totalCells} cells recalled`;
  }
  if (moduleName === 'flicker') {
    const hits = results.filter((r) => r.correct).length;
    return `${hits} / ${results.length} flicker hits`;
  }
  if (moduleName === 'sequencing') {
    const completed = results.filter((r) => r.completed).length;
    return `${completed} / ${results.length} rounds completed`;
  }
  if (moduleName === 'composure') {
    const distractionTrials = results.filter((r) => r.phase === 'distraction');
    const correct = distractionTrials.filter((r) => r.correct).length;
    if (!distractionTrials.length) return null;
    return `${Math.round((correct / distractionTrials.length) * 100)}% accuracy under distraction`;
  }
  if (moduleName === 'tilt') {
    const bettingTrials = results.filter((r) => r.prevOutcome != null);
    const rational = bettingTrials.filter(
      (r) => (r.prevOutcome === 'failure' && r.bet === 'low') || (r.prevOutcome === 'success' && r.bet === 'high')
    );
    if (!bettingTrials.length) return null;
    return `${Math.round((rational.length / bettingTrials.length) * 100)}% rational bets under pressure`;
  }
  return null;
}

/**
 * TestRunner — orchestrates all 4 modules in sequence.
 * Fetches trial sequences, renders the correct test component,
 * and submits data to the backend after each module.
 */
export default function TestRunner() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sessionId = searchParams.get('session');

  const genre = sessionStorage.getItem('nr_genre') || 'general';
  const MODULES = GENRE_MODULES[genre] || GENRE_MODULES.general;

  const [setupDone, setSetupDone] = useState(true);
  const [moduleIndex, setModuleIndex] = useState(0);
  const [trials, setTrials] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | running | submitting | break | error | finishing
  const [error, setError] = useState('');
  const [overallProgress, setOverallProgress] = useState(0);
  const [retryKey, setRetryKey] = useState(0); // incrementing retries the load effect
  const [breakStat, setBreakStat] = useState(null);
  const [tabWarning, setTabWarning] = useState(null); // { secondsAway }
  const tabViolations = useRef(0);
  const hiddenAt = useRef(null);

  // Redirect if no session ID
  useEffect(() => {
    if (!sessionId) {
      navigate('/');
    }
  }, [sessionId, navigate]);

  // Load trial sequence for the current module.
  // The `cancelled` flag guards against React StrictMode's double-invoke and
  // against the user clicking Retry while a previous request is still in flight.
  useEffect(() => {
    if (!sessionId || !setupDone || moduleIndex >= MODULES.length) return;

    const module = MODULES[moduleIndex];
    setStatus('loading');
    setTrials(null);
    setError('');

    let cancelled = false;

    getTrialSequence(sessionId, module)
      .then(({ trials: t }) => {
        if (!cancelled) {
          setTrials(t);
          setStatus('ready');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load test module. Check your connection and try again.');
          setStatus('error');
        }
      });

    return () => { cancelled = true; };
  }, [sessionId, moduleIndex, retryKey, setupDone]);

  // Tab visibility detection — only active while a test module is running
  useEffect(() => {
    const handleVisibility = () => {
      if (status !== 'ready') return;
      if (document.visibilityState === 'hidden') {
        hiddenAt.current = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAt.current) {
        const secondsAway = Math.round((Date.now() - hiddenAt.current) / 1000);
        hiddenAt.current = null;
        tabViolations.current += 1;
        setTabWarning({ secondsAway });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [status]);

  const handleModuleComplete = async (moduleName, trialResults) => {
    setStatus('submitting');
    setBreakStat(computeBreakStat(moduleName, trialResults));

    try {
      await submitModule(sessionId, moduleName, trialResults);
      setOverallProgress(((moduleIndex + 1) / MODULES.length) * 100);

      if (moduleIndex + 1 >= MODULES.length) {
        // All modules done — finalize
        setStatus('finishing');
        const result = await completeSession(sessionId);
        try { window.posthog?.capture('combine_completed', { genre, shareableId: result.shareableId, archetype: result.archetype?.id, score: result.scores?.overallScore }); } catch {}
        navigate('/complete', {
          replace: true,
          state: { shareableId: result.shareableId, scores: result.scores, archetype: result.archetype, genre },
        });
      } else {
        // Show break screen before next module
        setStatus('break');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit results. Please try again.');
      setStatus('error');
    }
  };

  const continueToNext = () => {
    setModuleIndex((prev) => prev + 1);
  };

  const handleAdminSkip = async () => {
    setStatus('finishing');
    try {
      const result = await adminAutoComplete(sessionId);
      navigate('/complete', {
        replace: true,
        state: { shareableId: result.shareableId, scores: result.scores, archetype: result.archetype, genre },
      });
    } catch (err) {
      setError('Admin skip failed: ' + (err.response?.data?.error || err.message));
      setStatus('error');
    }
  };

  const currentModule = MODULES[moduleIndex];

  if (!sessionId) return null;

  // Checklist removed — warnings now shown on homepage interstitial to reduce drop-off

  // --- Tab warning overlay (rendered on top of everything) ---
  if (tabWarning) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10,14,26,0.97)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
        <div className="card" style={{ maxWidth: 440, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ marginBottom: 8, color: 'var(--warning)' }}>Tab Switch Detected</h2>
          <p style={{ marginBottom: 8 }}>
            You left this test for <strong style={{ color: 'var(--text-primary)' }}>{tabWarning.secondsAway}s</strong>.
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            Switching tabs during a module can affect your timing and scores.
          </p>
          {tabViolations.current > 1 && (
            <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 8 }}>
              {tabViolations.current} tab switches detected this session.
            </p>
          )}
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 24 }}>
            Keep this tab focused for fair results.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn--primary"
              onClick={() => setTabWarning(null)}
            >
              Continue Test
            </button>
            <button className="btn btn--ghost" onClick={() => navigate('/')}>
              Abandon Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Render States ---

  if (status === 'error') {
    // If we haven't submitted any module data yet, allow retrying the load.
    // If the error happened during submission, only offer going back to start.
    const canRetryLoad = !error.toLowerCase().includes('submit') && !error.toLowerCase().includes('save');
    return (
      <div className="container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <div className="card" style={{ maxWidth: 440, margin: '0 auto' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ marginBottom: 24 }}>{error}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {canRetryLoad && (
              <button
                className="btn btn--primary"
                onClick={() => setRetryKey((k) => k + 1)}
              >
                Try Again
              </button>
            )}
            <button className="btn btn--ghost" onClick={() => navigate('/')}>
              Return to Start
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'finishing') {
    return (
      <div style={{
        minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px',
      }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div className="tr-spinner" style={{ margin: '0 auto 28px' }} />
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 10 }}>Generating Your Profile</h2>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Scoring your results and generating your neural profile report with AI analysis...
          </p>
          <div className="progress-bar" style={{ marginTop: 28, maxWidth: 280, margin: '28px auto 0' }}>
            <div className="progress-bar__fill" style={{ width: '100%', animation: 'pulse 1.8s ease-in-out infinite' }} />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'break') {
    const nextModule = MODULES[moduleIndex + 1];
    const doneColor  = MODULE_COLORS[currentModule] || 'var(--accent)';
    const nextColor  = MODULE_COLORS[nextModule]    || 'var(--accent)';

    return (
      <div className="container" style={{ paddingTop: 48, paddingBottom: 80, userSelect: 'none' }}>
        <div style={{ maxWidth: 540, margin: '0 auto' }}>

          {/* Module step tracker */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 40 }}>
            {MODULES.map((mod, i) => {
              const isDone    = i <= moduleIndex;
              const isCurrent = i === moduleIndex;
              const modColor  = MODULE_COLORS[mod] || 'var(--accent)';
              return (
                <React.Fragment key={mod}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      border: `2px solid ${isDone ? modColor : 'var(--border)'}`,
                      background: isDone ? modColor : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s',
                      fontSize: 13, fontWeight: 800,
                      color: isDone ? '#000' : 'var(--text-muted)',
                    }}>
                      {isDone ? '✓' : i + 1}
                    </div>
                    <span style={{
                      fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700,
                      color: isCurrent ? modColor : isDone ? 'var(--text-muted)' : 'var(--border)',
                      maxWidth: 60, textAlign: 'center', lineHeight: 1.3,
                    }}>
                      {MODULE_LABELS[mod].split(' ')[0]}
                    </span>
                  </div>
                  {i < MODULES.length - 1 && (
                    <div style={{
                      flex: 1, height: 2, margin: '0 4px', marginBottom: 22,
                      background: i < moduleIndex ? doneColor : 'var(--border)',
                      transition: 'background 0.4s',
                      minWidth: 20,
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Completed module card */}
          <div style={{
            padding: '24px', borderRadius: 14,
            border: `1px solid ${doneColor}44`,
            background: `${doneColor}08`,
            marginBottom: 16, textAlign: 'center',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 12, margin: '0 auto 16px',
              background: `${doneColor}18`, border: `1px solid ${doneColor}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: doneColor,
            }}>
              {MODULE_ICONS[currentModule]}
            </div>
            <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: doneColor, fontWeight: 700, marginBottom: 6 }}>
              Module Complete
            </p>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 16, color: 'var(--text-primary)' }}>
              {MODULE_LABELS[currentModule]}
            </h2>
            {breakStat && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: `${doneColor}14`, border: `1px solid ${doneColor}44`,
                borderRadius: 20, padding: '8px 20px',
                fontSize: 14, fontWeight: 700, color: doneColor,
              }}>
                <span>◆</span>
                <span>{breakStat}</span>
              </div>
            )}
          </div>

          {/* Next module preview */}
          {nextModule && (
            <div style={{
              padding: '20px 24px', borderRadius: 14,
              border: '1px solid var(--border)',
              background: 'var(--bg-card)',
              marginBottom: 24,
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                background: `${nextColor}14`, border: `1px solid ${nextColor}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: nextColor,
              }}>
                {MODULE_ICONS[nextModule]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, marginBottom: 3 }}>
                  Up Next
                </p>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>
                  {MODULE_LABELS[nextModule]}
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                  {MODULE_DESCS[nextModule]}
                </p>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: nextColor, flexShrink: 0 }}>
                {MODULES.length - moduleIndex - 1} left
              </span>
            </div>
          )}

          {/* Tab warning */}
          {tabViolations.current > 0 && (
            <p style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 16, textAlign: 'center' }}>
              {tabViolations.current} tab switch{tabViolations.current > 1 ? 'es' : ''} detected — scores may be affected.
            </p>
          )}

          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              Take a breath. Continue when ready.
            </p>
            <button className="btn btn--primary" onClick={continueToNext} style={{ fontSize: 15, padding: '13px 52px' }}>
              Continue →
            </button>
          </div>

        </div>
      </div>
    );
  }

  if (status === 'loading' || status === 'submitting') {
    return (
      <div style={{
        minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="tr-spinner" style={{ margin: '0 auto 20px' }} />
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {status === 'submitting' ? 'Saving results...' : 'Loading module...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 32, paddingBottom: 48, userSelect: 'none' }}>
      {/* Admin skip button */}
      {user?.isAdmin && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}>
          <button
            className="btn btn--ghost"
            onClick={handleAdminSkip}
            style={{ fontSize: 12, padding: '8px 16px', color: '#f59e0b', borderColor: '#f59e0b44', background: 'var(--bg)' }}
          >
            ⚡ Skip
          </button>
        </div>
      )}
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span className="text-muted" style={{ fontSize: 13 }}>
            Module {moduleIndex + 1} of {MODULES.length} — {MODULE_LABELS[currentModule]}
          </span>
          <span className="text-muted" style={{ fontSize: 13 }}>
            {Math.round(overallProgress)}% complete
          </span>
        </div>
        <div className="progress-bar">
          <div className="progress-bar__fill" style={{ width: `${overallProgress}%` }} />
        </div>
      </div>

      {/* Active Test Module */}
      {status === 'ready' && trials && (() => {
        const moduleNum = moduleIndex + 1;
        const totalModules = MODULES.length;
        const sharedProps = { trials, moduleNum, totalModules };
        return (
          <>
            {currentModule === 'reaction' && (
              <ReactionTest icon={MODULE_ICONS.reaction} {...sharedProps} onComplete={(results) => handleModuleComplete('reaction', results)} />
            )}
            {currentModule === 'aim' && (
              <AimTest icon={MODULE_ICONS.aim} {...sharedProps} onComplete={(results) => handleModuleComplete('aim', results)} />
            )}
            {currentModule === 'tracking' && (
              <TrackingTest icon={MODULE_ICONS.tracking} {...sharedProps} onComplete={(results) => handleModuleComplete('tracking', results)} />
            )}
            {currentModule === 'memory' && (
              <MemoryTest icon={MODULE_ICONS.memory} {...sharedProps} onComplete={(results) => handleModuleComplete('memory', results)} />
            )}
            {currentModule === 'flicker' && (
              <FlickerTest icon={MODULE_ICONS.flicker} {...sharedProps} onComplete={(results) => handleModuleComplete('flicker', results)} />
            )}
            {currentModule === 'sequencing' && (
              <SequencingTest icon={MODULE_ICONS.sequencing} {...sharedProps} onComplete={(results) => handleModuleComplete('sequencing', results)} />
            )}
            {currentModule === 'composure' && (
              <ComposureTest icon={MODULE_ICONS.composure} {...sharedProps} onComplete={(results) => handleModuleComplete('composure', results)} />
            )}
            {currentModule === 'tilt' && (
              <TiltTest icon={MODULE_ICONS.tilt} {...sharedProps} onComplete={(results) => handleModuleComplete('tilt', results)} />
            )}
          </>
        );
      })()}
    </div>
  );
}
